import { useEffect, useMemo, useState } from "react"
import {
  CheckIcon,
  CodeBracketIcon,
  EnvelopeIcon,
  EyeIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"
import { Button } from "@/components/Button"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import {
  defaultEmailPreviewParams,
  type EmailPreviewParams,
  type EmailPreviewTemplateId,
  type EmailPreviewUrlMode,
  useEmailPreview,
  useEmailPreviewTemplates,
} from "@/hooks/useEmailPreviews"

type PreviewTab = "html" | "text" | "source"

const urlModeOptions: Array<{
  value: EmailPreviewUrlMode
  label: string
}> = [
  { value: "current", label: "Current" },
  { value: "development", label: "Dev" },
  { value: "production", label: "Prod" },
]

const sampleFields: Array<{
  key: keyof Omit<EmailPreviewParams, "urlMode">
  label: string
  type?: "email" | "number" | "text" | "date"
}> = [
  { key: "teamName", label: "Team" },
  { key: "inviterName", label: "Inviter" },
  { key: "inviterEmail", label: "Inviter email", type: "email" },
  { key: "inviteeName", label: "Recipient" },
  { key: "inviteeEmail", label: "Recipient email", type: "email" },
  { key: "senderName", label: "Film sender" },
  { key: "sourceTeamName", label: "Source team" },
  { key: "gameTitle", label: "Game title" },
  { key: "exchangeMessage", label: "Exchange message" },
  { key: "totalClips", label: "Export clips", type: "number" },
  { key: "exportError", label: "Export error" },
  { key: "coachFirstName", label: "Coach first name" },
  { key: "compEndDate", label: "Welcome-offer comp end date", type: "date" },
]

const isEmailLike = (value: string) => /\S+@\S+\.\S+/.test(value.trim())

export function EmailTemplates() {
  const {
    data: templateData,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useEmailPreviewTemplates()
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<EmailPreviewTemplateId>("team-invitation")
  const [params, setParams] = useState<EmailPreviewParams>(defaultEmailPreviewParams)
  const [activeTab, setActiveTab] = useState<PreviewTab>("html")
  const [copied, setCopied] = useState<"subject" | "text" | null>(null)

  useEffect(() => {
    if (templateData?.defaults) {
      setParams((current) => ({ ...templateData.defaults, urlMode: current.urlMode }))
    }
  }, [templateData?.defaults])

  const selectedTemplate = useMemo(
    () =>
      templateData?.templates.find((template) => template.id === selectedTemplateId) ??
      templateData?.templates[0],
    [selectedTemplateId, templateData?.templates]
  )

  // The welcome-offer templates read the coach name (and, for team-ready, the
  // end date). A blank there must disable preview and copy rather than fall
  // back to the sample values, or the admin could copy an email addressed to
  // the sample coach. Other templates ignore these fields.
  const welcomeFieldsOk =
    !selectedTemplateId.startsWith("welcome-offer-") ||
    (params.coachFirstName.trim().length > 0 &&
      (selectedTemplateId !== "welcome-offer-team-ready" ||
        /^\d{4}-\d{2}-\d{2}$/.test(params.compEndDate.trim())))

  const canPreview =
    welcomeFieldsOk &&
    params.teamName.trim().length > 0 &&
    params.inviterName.trim().length > 0 &&
    params.inviteeName.trim().length > 0 &&
    params.senderName.trim().length > 0 &&
    params.sourceTeamName.trim().length > 0 &&
    params.gameTitle.trim().length > 0 &&
    params.exportError.trim().length > 0 &&
    params.totalClips > 0 &&
    isEmailLike(params.inviterEmail) &&
    isEmailLike(params.inviteeEmail)

  const {
    data: preview,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useEmailPreview(selectedTemplateId, params, canPreview)

  const handleCopy = async (kind: "subject" | "text") => {
    if (!preview) return
    const value = kind === "subject" ? preview.email.subject : preview.email.message.trim()
    await navigator.clipboard.writeText(value)
    setCopied(kind)
    setTimeout(() => setCopied((current) => (current === kind ? null : current)), 1600)
  }

  if (templatesError) {
    return <ErrorFallback error={templatesError as Error} />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-secondary">
          Render backend email templates with realistic team and member data.
        </p>
        {preview && (
          <div className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
            <span className="text-secondary">Links use </span>
            <span className="font-medium text-primary">{preview.frontendUrl}</span>
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-6">
          <section className="rounded-lg border border-border bg-surface p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-secondary">
              Templates
            </h2>
            <div className="space-y-2">
              {isLoadingTemplates
                ? Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="h-20 animate-pulse rounded-md bg-background" />
                  ))
                : templateData?.templates.map((template) => {
                    const isSelected = template.id === selectedTemplateId
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(template.id)}
                        className={`w-full rounded-md border p-3 text-left transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10"
                            : "border-border bg-background hover:border-primary/50"
                        }`}
                      >
                        <span className="flex items-start gap-3">
                          <EnvelopeIcon
                            className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                              isSelected ? "text-primary" : "text-secondary"
                            }`}
                          />
                          <span>
                            <span className="block font-medium text-primary">{template.name}</span>
                            <span className="mt-1 block text-xs text-secondary">
                              {template.audience}
                            {template.onHold ? " · on hold" : ""}
                            </span>
                          </span>
                        </span>
                      </button>
                    )
                  })}
            </div>
          </section>

          <section className="rounded-lg border border-border bg-surface p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-secondary">
              Links
            </h2>
            <div className="grid grid-cols-3 rounded-md border border-border bg-background p-1">
              {urlModeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setParams((current) => ({ ...current, urlMode: option.value }))}
                  className={`rounded px-3 py-2 text-sm font-medium transition-colors ${
                    params.urlMode === option.value
                      ? "bg-primary text-contrast"
                      : "text-secondary hover:text-primary"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-border bg-surface p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-secondary">
              Sample Data
            </h2>
            <div className="space-y-3">
              {sampleFields.map((field) => (
                <label key={field.key} className="block">
                  <span className="mb-1 block text-sm font-medium text-primary">{field.label}</span>
                  <input
                    type={field.type ?? "text"}
                    value={String(params[field.key])}
                    onChange={(event) =>
                      setParams((current) => ({
                        ...current,
                        [field.key]:
                          field.type === "number" ? Number(event.target.value) : event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-secondary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </label>
              ))}
            </div>
          </section>
        </aside>

        <section className="min-w-0 rounded-lg border border-border bg-surface">
          <div className="border-b border-border p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-primary">
                  {selectedTemplate?.name ?? "Email"}
                </h2>
                {selectedTemplate && (
                  <p className="mt-1 text-sm text-secondary">{selectedTemplate.description}</p>
                )}
                {selectedTemplate?.onHold && (
                  <p
                    className="mt-2 inline-flex items-center rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                    data-testid="email-template-on-hold"
                  >
                    On hold: preview only, not for sending. Copy is disabled.
                  </p>
                )}
              </div>
              {preview && !selectedTemplate?.onHold && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleCopy("subject")}>
                    {copied === "subject" ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                    Subject
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopy("text")}>
                    {copied === "text" ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                    Plain text
                  </Button>
                </div>
              )}
            </div>
          </div>

          {!canPreview ? (
            <div className="p-8 text-sm text-secondary">
              Enter valid sample names and email addresses to render the preview.
            </div>
          ) : previewError ? (
            <div className="p-6">
              <ErrorFallback error={previewError as Error} />
            </div>
          ) : isLoadingPreview || !preview ? (
            <div className="space-y-4 p-6">
              <div className="h-6 w-2/3 animate-pulse rounded bg-background" />
              <div className="h-10 animate-pulse rounded bg-background" />
              <div className="h-[520px] animate-pulse rounded bg-background" />
            </div>
          ) : (
            <div className="space-y-4 p-4">
              <dl className="grid gap-3 text-sm lg:grid-cols-4">
                <div className="rounded-md border border-border bg-background p-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    To
                  </dt>
                  <dd className="mt-1 break-words text-primary">{preview.email.to_email}</dd>
                </div>
                <div className="rounded-md border border-border bg-background p-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    From
                  </dt>
                  <dd className="mt-1 break-words text-primary">{preview.email.from_email}</dd>
                </div>
                <div className="rounded-md border border-border bg-background p-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    Subject
                  </dt>
                  <dd className="mt-1 break-words text-primary">{preview.email.subject}</dd>
                </div>
                <div className="rounded-md border border-border bg-background p-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    Preheader
                  </dt>
                  <dd className="mt-1 break-words text-primary">
                    {preview.preheader ?? preview.email.preheader ?? "None"}
                  </dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-2 border-b border-border">
                <button
                  type="button"
                  onClick={() => setActiveTab("html")}
                  className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                    activeTab === "html"
                      ? "border-primary text-primary"
                      : "border-transparent text-secondary hover:text-primary"
                  }`}
                >
                  <EyeIcon className="h-4 w-4" />
                  HTML
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("text")}
                  className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                    activeTab === "text"
                      ? "border-primary text-primary"
                      : "border-transparent text-secondary hover:text-primary"
                  }`}
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  Text
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("source")}
                  className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                    activeTab === "source"
                      ? "border-primary text-primary"
                      : "border-transparent text-secondary hover:text-primary"
                  }`}
                >
                  <CodeBracketIcon className="h-4 w-4" />
                  Source
                </button>
              </div>

              {activeTab === "html" && (
                <iframe
                  title={`${selectedTemplate?.name ?? "Email"} HTML preview`}
                  srcDoc={preview.previewHtml}
                  sandbox=""
                  className="h-[640px] w-full rounded-md border border-border bg-white"
                />
              )}

              {activeTab === "text" && (
                <pre className="min-h-[420px] whitespace-pre-wrap rounded-md border border-border bg-background p-4 text-sm leading-6 text-primary">
                  {preview.email.message.trim()}
                </pre>
              )}

              {activeTab === "source" && (
                <pre className="min-h-[420px] overflow-auto rounded-md border border-border bg-background p-4 text-xs leading-5 text-primary">
                  {preview.email.message_html ?? preview.previewHtml}
                </pre>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
